'use client';

/**
 * NeuroplasticSectionRenderer - Pure JSON-driven section rendering
 * 
 * This component renders sections entirely from JSON templates stored in the database.
 * NO hardcoded React component imports - everything is dynamic.
 * 
 * Architecture:
 * - Section.template (JSON) → TemplateEngine → React elements
 * - Section.sectionData (JSON) → Data bindings for template
 * - Section.styles (JSON) → CSS styles
 * - Section.background (JSON) → Background configuration
 * 
 * This enables:
 * - Full admin editability of section design
 * - No code changes needed for new section types
 * - AI-driven section generation (neuroplasticity)
 */

import React, { useMemo, useEffect, useState } from 'react';
import { TemplateEngine } from './dynamic/TemplateEngine';
import { TemplateNode, SectionData } from '@/types/template-schema';
// PERFORMANCE: Lazy load template-generators (4963 lines) - only needed when generating templates
// Most sections have explicit templates, so we can defer this import
let templateGeneratorsModule: typeof import('@/scripts/template-generators') | null = null;
const getTemplateGenerator = async (sectionType: string) => {
  if (!templateGeneratorsModule) {
    templateGeneratorsModule = await import('@/scripts/template-generators');
  }
  return templateGeneratorsModule.generateTemplateForSectionType(sectionType);
};
import { SectionProvider } from '@/contexts/SectionContext';
import { useBackgroundOptimization } from '@/hooks/useBackgroundOptimization';
import { mediaCache } from '@/lib/media-cache';
import { ResponsiveBackgroundImage } from '@/types/sections';

// ============================================================================
// Types
// ============================================================================

export interface NeuroplasticSection {
  id: string;
  sectionKey: string;
  title?: string | null;
  type?: string | null;
  template?: TemplateNode | Record<string, unknown> | null;
  sectionData?: SectionData | Record<string, unknown> | null;
  styles?: Record<string, unknown> | null;
  background?: {
    type?: 'COLOR' | 'IMAGE';
    value?: string;
    opacity?: number;
  } | Record<string, unknown> | null;
  isActive: boolean;
  order?: number;
}

export interface NeuroplasticSectionRendererProps {
  section: NeuroplasticSection;
  isFirstSection?: boolean;
  sectionIndex?: number;
  mediaUrls?: string[];
  debug?: boolean;
  /** Optional styles to apply to content instead of computing from section */
  containerStyle?: React.CSSProperties;
  /** When false, background is handled externally (e.g., SectionRenderer wrapper) */
  applyBackground?: boolean;
}

// ============================================================================
// Style Computation
// ============================================================================

function computeSectionStyles(
  background?: NeuroplasticSection['background'],
  styles?: Record<string, unknown> | null
): React.CSSProperties {
  const computedStyles: React.CSSProperties = {};

  // Process background
  if (background && typeof background === 'object') {
    if ('type' in background && 'value' in background) {
      if (background.type === 'COLOR' && background.value) {
        const opacity = typeof background.opacity === 'number' ? background.opacity : 1;
        if (opacity < 1) {
          const hex = background.value as string;
          const rgb = hexToRgb(hex);
          if (rgb) {
            computedStyles.backgroundColor = `rgba(${rgb.join(', ')}, ${opacity})`;
          }
        } else {
          computedStyles.backgroundColor = background.value as string;
        }
      } else if (background.type === 'IMAGE' && background.value) {
        computedStyles.backgroundImage = `url(${background.value})`;
        computedStyles.backgroundSize = 'cover';
        computedStyles.backgroundPosition = 'center';
        computedStyles.backgroundRepeat = 'no-repeat';
      }
    } else {
      // Raw CSS background object
      Object.assign(computedStyles, background);
    }
  }

  // Process styles
  if (styles && typeof styles === 'object') {
    // Handle structured styles
    if ('layout' in styles && typeof styles.layout === 'object') {
      const layout = styles.layout as Record<string, string>;
      if (layout.width) computedStyles.width = layout.width;
      if (layout.height) computedStyles.height = layout.height;
      if (layout.padding) computedStyles.padding = layout.padding;
      if (layout.margin) computedStyles.margin = layout.margin;
      if (layout.display) computedStyles.display = layout.display as React.CSSProperties['display'];
      if (layout.justifyContent) computedStyles.justifyContent = layout.justifyContent as React.CSSProperties['justifyContent'];
      if (layout.alignItems) computedStyles.alignItems = layout.alignItems as React.CSSProperties['alignItems'];
    }

    if ('typography' in styles && typeof styles.typography === 'object') {
      const typography = styles.typography as Record<string, string>;
      if (typography.fontSize) computedStyles.fontSize = typography.fontSize;
      if (typography.fontWeight) computedStyles.fontWeight = typography.fontWeight;
      if (typography.color) computedStyles.color = typography.color;
      if (typography.textAlign) computedStyles.textAlign = typography.textAlign as React.CSSProperties['textAlign'];
    }

    if ('appearance' in styles && typeof styles.appearance === 'object') {
      const appearance = styles.appearance as Record<string, string>;
      if (appearance.backgroundColor) computedStyles.backgroundColor = appearance.backgroundColor;
      if (appearance.borderRadius) computedStyles.borderRadius = appearance.borderRadius;
    }

    // Handle textColor specially
    if ('textColor' in styles) {
      computedStyles.color = styles.textColor as string;
    }

    // If no structured keys, treat as raw CSS
    if (!('layout' in styles || 'typography' in styles || 'appearance' in styles || 'textColor' in styles)) {
      Object.assign(computedStyles, styles);
    }
  }

  return computedStyles;
}

/**
 * Split computed styles into outer (background/sizing) and content (padding/typography/layout)
 * so padding stays inside the background layer.
 */
function splitStylesForLayers(styles: React.CSSProperties) {
  const outerStyles: React.CSSProperties = {};
  const contentStyles: React.CSSProperties = {};

  const outerKeys = new Set([
    'margin',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
    'width',
    'height',
    'minWidth',
    'maxWidth',
    'minHeight',
    'maxHeight',
    'background',
    'backgroundColor',
    'backgroundImage',
    'backgroundSize',
    'backgroundPosition',
    'backgroundRepeat',
    'backgroundAttachment',
    'backgroundClip',
    'backgroundOrigin',
    'backgroundBlendMode',
    'borderRadius',
    'borderColor',
    'borderWidth',
    'borderStyle',
    'boxShadow',
    'overflow',
    'position',
    'top',
    'right',
    'bottom',
    'left',
  ]);

  Object.entries(styles).forEach(([key, value]) => {
    if (value === undefined) return;
    if (outerKeys.has(key)) {
      (outerStyles as Record<string, unknown>)[key] = value;
    } else {
      (contentStyles as Record<string, unknown>)[key] = value;
    }
  });

  return { outerStyles, contentStyles };
}

function hexToRgb(hex: string): [number, number, number] | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : null;
}

// ============================================================================
// Main Component
// ============================================================================

export function NeuroplasticSectionRenderer({
  section,
  isFirstSection = false,
  sectionIndex = 0,
  mediaUrls = [],
  debug = false,
  containerStyle,
  applyBackground = true,
}: NeuroplasticSectionRendererProps): React.ReactElement | null {
  // Get background image from sectionData
  const backgroundImage = useMemo(() => {
    const sectionData = section.sectionData as Record<string, unknown> | undefined;
    if (!sectionData?.backgroundImage) return undefined;
    
    const bg = sectionData.backgroundImage;
    if (typeof bg === 'string' || (typeof bg === 'object' && bg !== null && ('desktop' in bg || 'mobile' in bg))) {
      return bg as ResponsiveBackgroundImage | string;
    }
    return undefined;
  }, [section.sectionData]);

  // Prioritize first 3 sections
  const shouldPrioritize = isFirstSection || sectionIndex < 3;

  // Preload media
  useEffect(() => {
    if (shouldPrioritize && mediaUrls.length > 0) {
      mediaCache.preloadBatch(mediaUrls, 'high').catch((err) => {
        console.debug('[Neuroplastic] Media preload error:', err);
      });
    } else if (mediaUrls.length > 0) {
      mediaCache.addToQueue(mediaUrls);
      mediaCache.processQueue('low').catch((err) => {
        console.debug('[Neuroplastic] Media queue error:', err);
      });
    }
  }, [shouldPrioritize, mediaUrls]);

  // Background optimization
  const bgOptimization = useBackgroundOptimization(backgroundImage, {
    lazyLoad: !shouldPrioritize,
    priority: shouldPrioritize,
    rootMargin: '200px',
  });

  // Compute styles
  const { outerStyles, contentStyles } = useMemo(() => {
    if (containerStyle !== undefined) {
      return { outerStyles: {}, contentStyles: containerStyle };
    }

    const computed = computeSectionStyles(
      applyBackground ? (section.background as NeuroplasticSection['background']) : null,
      section.styles
    );

    return splitStylesForLayers(computed);
  }, [containerStyle, applyBackground, section.background, section.styles]);

  const wrapWithContentStyles = (node: React.ReactNode) => {
    if (Object.keys(contentStyles).length === 0) return node;
    return <div style={contentStyles}>{node}</div>;
  };

  // Get or generate template
  // PERFORMANCE: Use existing template first (most common case)
  // Only generate template if needed (lazy-loaded to reduce initial bundle)
  const [generatedTemplate, setGeneratedTemplate] = useState<TemplateNode | null>(null);
  
  useEffect(() => {
    // Only generate if section has no explicit template but has a type
    const hasExplicitTemplate = section.template && typeof section.template === 'object' && 'type' in section.template;
    
    if (!hasExplicitTemplate && section.type) {
      // Lazy-load template generator only when needed
      getTemplateGenerator(section.type)
        .then((generated) => {
          if (generated) {
            if (debug) {
              console.log(`[Neuroplastic] Auto-generated template for section type: ${section.type}`);
            }
            setGeneratedTemplate(generated);
          } else {
            setGeneratedTemplate(null);
          }
        })
        .catch((err) => {
          console.debug('[Neuroplastic] Template generation error:', err);
          setGeneratedTemplate(null);
        });
    } else {
      setGeneratedTemplate(null);
    }
  }, [section.template, section.type, debug]);
  
  const template = useMemo((): TemplateNode | null => {
    // Use existing template if available (most common case - no async needed)
    if (section.template && typeof section.template === 'object' && 'type' in section.template) {
      return section.template as TemplateNode;
    }

    // Use generated template (async-loaded)
    return generatedTemplate;
  }, [section.template, generatedTemplate]);

  // Early return for inactive sections (after hooks)
  if (!section.isActive) {
    return null;
  }

  // Prepare section data for template bindings
  const sectionData = (section.sectionData || {}) as SectionData;

  // Debug mode: show section info when no template
  if (!template) {
    if (debug) {
      return (
        <section
          data-section={section.sectionKey}
          data-section-id={section.id}
          className="py-16 px-4 bg-zinc-100"
        >
          <div className="container mx-auto max-w-4xl text-center">
            <p className="text-zinc-500 mb-2">Section: {section.title || section.sectionKey}</p>
            <p className="text-zinc-400 text-sm">Type: {section.type || 'Unknown'}</p>
            <p className="text-zinc-400 text-sm">No template defined</p>
            <pre className="mt-4 p-4 bg-zinc-200 rounded text-left text-xs overflow-auto">
              {JSON.stringify(sectionData, null, 2)}
            </pre>
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <SectionProvider sectionId={section.id}>
      <div
        ref={(el) => {
          if (applyBackground && 'setElementRef' in bgOptimization && typeof bgOptimization.setElementRef === 'function') {
            bgOptimization.setElementRef(el);
          }
        }}
        data-section={section.sectionKey}
        data-section-id={section.id}
        data-section-type={section.type}
        style={outerStyles}
      >
        {wrapWithContentStyles(
          <TemplateEngine
            template={template}
            sectionData={sectionData}
            additionalContext={{
              sectionId: section.id,
              sectionKey: section.sectionKey,
              sectionType: section.type,
              isFirstSection,
              sectionIndex,
            }}
            debug={debug}
          />
        )}
      </div>
    </SectionProvider>
  );
}

export default NeuroplasticSectionRenderer;
